import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ThirdPartyLogo from '../components/ThirdPartyLogo';
import { BRAND_FONT_STACK } from '../constants/thirdPartyPages';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─────────────────────────────────────────────────────────────────────────────
// Verification of Mortgage — the private lender's page (/vom?token=…). 🔑 (Jesse, 2026-10-28 — the approved proposal B)
// The lender uploads a completed, signed VOM (any formal VOM, or the pre-filled Doorly one downloaded here) and fills
// only the lender details we don't have (name, phone, mailing address; "Company (if any)" always, optional; never the
// email). The upload is read at once (backend utils/privateVomUpload): unreadable or incomplete → this page asks again
// with a plain message. Same shell as before (logo, blue header card — white title, borrower strip, footer).
// ─────────────────────────────────────────────────────────────────────────────
const FIELD_LABELS = { name: 'Lender Name', company: 'Company (if any)', phone: 'Phone Number', address: 'Mailing Address' };
const PLACEHOLDERS = { name: 'Lender / note-holder name', company: 'Company name', phone: '512-555-0100', address: '123 Main St, Austin, TX 78701' };
const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const kindOf = (f) => (/\.pdf$/i.test(f.name) ? 'PDF' : /\.png$/i.test(f.name) ? 'PNG' : 'JPG');

export default function VOMForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const fileRef = useRef(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [file, setFile] = useState(null);
  const [details, setDetails] = useState({ name: '', company: '', phone: '', address: '' });

  useEffect(() => {
    if (!token) { setError('Missing verification token.'); setLoading(false); return; }
    fetch(`${API_URL}/api/vom/validate/${token}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error === 'used') { setSubmitted({ already: true }); setLoading(false); return; }
        if (!data.valid) { setError(data.error === 'expired' || data.error === 'closed' ? 'This verification link is no longer active.' : 'Invalid verification link.'); setLoading(false); return; }
        setInfo(data);
        setLoading(false);
      })
      .catch(() => { setError('Could not load the verification page.'); setLoading(false); });
  }, [token]);

  const missing = (info && Array.isArray(info.missing)) ? info.missing : [];
  // The fields shown: what is missing, plus the company (always, optional).
  const fields = [...missing.filter((k) => k === 'name'), 'company', ...missing.filter((k) => k !== 'name')];
  const required = (k) => k !== 'company' && missing.includes(k);
  const detailsReady = missing.every((k) => (k === 'phone' ? details.phone.replace(/\D/g, '').length >= 10 : k === 'address' ? details.address.trim().length >= 6 : details[k].trim().length >= 2));
  const ready = !!file && detailsReady && !submitting;

  const choose = (f) => {
    setSubmitError(null);
    if (!f) return;
    if (!/\.(pdf|jpe?g|png)$/i.test(f.name)) { setSubmitError('Upload a PDF, JPG or PNG file.'); return; }
    if (f.size > 25 * 1024 * 1024) { setSubmitError('That file is too large. Upload a file under 25 MB.'); return; }
    setFile(f);
  };
  const removeFile = () => { setFile(null); if (fileRef.current) fileRef.current.value = ''; };

  const handleSubmit = async () => {
    if (!ready) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('lender_name', details.name);
      fd.append('lender_company', details.company);
      fd.append('lender_phone', details.phone);
      fd.append('lender_address', details.address);
      const res = await fetch(`${API_URL}/api/vom/upload/${token}`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setSubmitted({ fileName: data.fileName || file.name }); return; }
      if (data.askAgain) removeFile();
      setSubmitError(data.error || 'Submission failed.');
    } catch { setSubmitError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', fontFamily: BRAND_FONT_STACK, padding: '32px 16px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <ThirdPartyLogo height={36} />
        </div>
        {children}
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '11px', color: '#9ca3af' }}>
          Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity
        </div>
      </div>
    </div>
  );

  if (loading) return wrap(<div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>Loading...</div>);

  if (error) return wrap(
    <div style={{ background: '#fff', borderRadius: '16px', padding: '48px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Link Invalid</div>
      <div style={{ fontSize: '14px', color: '#9ca3af' }}>{error}</div>
    </div>
  );

  if (submitted) return wrap(
    <div data-testid="vom-done" style={{ background: '#fff', borderRadius: '16px', padding: '48px 40px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
      <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', border: '3px solid #3a6ad9' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>
      </div>
      <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '800', color: '#111827' }}>Verification Submitted</h2>
      <p data-testid="vom-done-text" style={{ margin: 0, fontSize: '15px', color: '#6b7280', lineHeight: '1.6' }}>
        {submitted.already
          ? 'Thank you! We’ve received the signed Verification of Mortgage for this loan. Nothing else is needed from you.'
          : `Thank you! We've received the signed Verification of Mortgage for ${(info && info.borrowerFullName) || 'this loan'} (${submitted.fileName}). Nothing else is needed from you.`}
      </p>
    </div>
  );

  const inputStyle = { width: '100%', padding: '11px 13px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '15px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', background: '#fff' };
  const labelStyle = { fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const sectionStyle = { fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', paddingBottom: '8px', borderBottom: '1px solid #f3f4f6', marginTop: '4px' };

  return wrap(
    <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' }}>
      <div style={{ background: '#3a6ad9', padding: '24px 28px' }}>
        <h1 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '800', color: '#ffffff' }}>Verification of Mortgage</h1>
        <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.75)' }}>
          Upload a completed, signed Verification of Mortgage for this loan. It goes directly to Go Doorly, LLC.
        </p>
      </div>

      {info && (
        <div style={{ background: '#eff6ff', borderBottom: '1px solid #bfdbfe', padding: '14px 28px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div data-testid="vom-borrower" style={{ fontSize: '13px', color: '#374151' }}>
            <span style={{ fontWeight: '700' }}>Borrower: </span>{info.borrowerFullName}
          </div>
          {info.loanNumber && (
            <div style={{ fontSize: '13px', color: '#374151' }}>
              <span style={{ fontWeight: '700' }}>Loan #: </span>{info.loanNumber}
            </div>
          )}
        </div>
      )}

      <div style={{ padding: '28px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={sectionStyle}>Verification of Mortgage</div>
            <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>Use your own Verification of Mortgage, or download ours: it's pre-filled with the loan details. Complete it, sign it and upload it below. Any formal Verification of Mortgage is accepted.</div>
            <a data-testid="vom-template" href={`${API_URL}/api/vom/template/${encodeURIComponent(token)}`} download
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: '10px', color: '#3a6ad9', fontWeight: '700', fontSize: '14px', textDecoration: 'none', background: '#fff' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3a6ad9" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M5 21h14"/></svg>
              Pre-filled Verification of Mortgage (PDF)
            </a>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label htmlFor="vom-file" style={labelStyle}>Signed Verification of Mortgage <span style={{ color: '#ff082f' }}>*</span></label>
            <input id="vom-file" ref={fileRef} data-testid="vom-file" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" style={{ display: 'none' }}
              onChange={(e) => choose(e.target.files && e.target.files[0])} />
            {!file ? (
              <button type="button" data-testid="vom-choose" onClick={() => fileRef.current && fileRef.current.click()}
                style={{ border: '2px dashed #d1d5db', borderRadius: '10px', background: '#fafafa', padding: '22px 16px', textAlign: 'center', cursor: 'pointer', fontFamily: 'inherit' }}>
                <div style={{ fontSize: '14px', color: '#374151', fontWeight: '700', marginBottom: '4px' }}>Choose a file to upload</div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>PDF, JPG or PNG · every page, including the signature page</div>
              </button>
            ) : (
              <div data-testid="vom-chosen" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: '10px', background: '#fafafa' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827', overflowWrap: 'anywhere' }}>{file.name}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{kindOf(file)} · {fmtSize(file.size)}</div>
                </div>
                <button type="button" data-testid="vom-remove" onClick={removeFile} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#9ca3af', fontWeight: '600', flex: 'none' }}>Remove</button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={sectionStyle}>Lender / Servicer Information</div>
            <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.5' }}>We just need these to finish your details.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '14px' }}>
              {fields.map((k) => (
                <div key={k}>
                  <label htmlFor={`vom-${k}`} style={labelStyle}>{FIELD_LABELS[k]}{required(k) && <span style={{ color: '#ff082f' }}> *</span>}</label>
                  <input id={`vom-${k}`} data-testid={`vom-${k}`} style={inputStyle} value={details[k]} placeholder={PLACEHOLDERS[k]} type={k === 'phone' ? 'tel' : 'text'}
                    onChange={(e) => setDetails((p) => ({ ...p, [k]: e.target.value }))} />
                </div>
              ))}
            </div>
          </div>

          {submitError && (
            <div role="alert" data-testid="vom-error" style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#ff082f', fontWeight: '600' }}>{submitError}</div>
          )}

          <button type="button" data-testid="vom-submit" onClick={handleSubmit} disabled={!ready}
            style={{
              width: '100%', padding: '16px',
              background: ready ? '#3a6ad9' : '#e5e7eb',
              color: ready ? '#ffffff' : '#9ca3af',
              border: 'none', borderRadius: '12px',
              cursor: ready ? 'pointer' : 'not-allowed',
              fontWeight: '800', fontSize: '16px', fontFamily: 'inherit',
            }}>
            {submitting ? 'Submitting...' : 'Submit Verification'}
          </button>
        </div>
      </div>
    </div>
  );
}
